import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { obtenerPrograma } from '../../servicios/programaService';
import {
  obtenerConfigRegistroCalidad, actualizarAvanceCalidad
} from '../../servicios/calidadService';
import ConfiguracionRegistros from './ConfiguracionRegistros';
import '../estilos/Calidad.css';

const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  observado: 'Observado',
  aprobado: 'Aprobado'
};

const ESTADO_CLASE = {
  pendiente: 'pendiente',
  observado: 'atrasado',
  aprobado: 'completado'
};

function estadoProtocolo(act, protocolo) {
  return (act.registroCalidadProtocolos || []).find(p => p.protocolo === protocolo)
    || { protocolo, estado: 'pendiente', observacion: '' };
}

const CARGOS_CONTROL = ['Jefe de terreno', 'Calidad', 'Administrador de obra'];

function FilaRegistro({ act, protocolo, cargosAsignados, onActualizarProtocolo, primeraFila, rowSpan }) {
  const item = estadoProtocolo(act, protocolo);
  const estado = item.estado || 'pendiente';
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [escribiendoObs, setEscribiendoObs] = useState(false);
  const [textoObs, setTextoObs] = useState('');
  const [menuAbierto, setMenuAbierto] = useState(false);

  const marcarAprobado = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onActualizarProtocolo(act, protocolo, { estado: 'aprobado', observacion: '' });
      setMenuAbierto(false);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const abrirObservacion = () => {
    setTextoObs(item.observacion || '');
    setError(null);
    setMenuAbierto(false);
    setEscribiendoObs(true);
  };

  const guardarObservacion = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onActualizarProtocolo(act, protocolo, { estado: 'observado', observacion: textoObs });
      setEscribiendoObs(false);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const volverAPendiente = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onActualizarProtocolo(act, protocolo, { estado: 'pendiente', observacion: '' });
      setMenuAbierto(false);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <tr className={primeraFila ? 'calidad-fila-partida-inicio' : ''}>
      {primeraFila && (
        <td rowSpan={rowSpan} style={{ fontWeight: 500, maxWidth: 280, color: '#000', verticalAlign: 'top' }}>
          {act._rutaGrupos?.length > 0 && (
            <div className="calidad-ruta-grupos">
              {act._rutaGrupos.map((n, i) => (
                <span key={i}>
                  {i > 0 && <span className="calidad-ruta-sep">›</span>}
                  <span className="calidad-ruta-item">{n}</span>
                </span>
              ))}
            </div>
          )}
          {act.nombre}
        </td>
      )}
      <td style={{ fontSize: 13, color: '#374151' }}>{protocolo}</td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span className={`calidad-estado calidad-estado-${ESTADO_CLASE[estado]}`}>
            {ESTADO_LABEL[estado]}
          </span>
          {estado === 'observado' && (
            <span className="calidad-chip calidad-chip-atrasada">Supervisor</span>
          )}
        </div>
      </td>
      {primeraFila && (
        <td rowSpan={rowSpan} style={{ verticalAlign: 'top' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(cargosAsignados || []).map(cargo => (
              <span key={cargo} className="calidad-chip calidad-chip-total">{cargo}</span>
            ))}
          </div>
        </td>
      )}
      <td style={{ whiteSpace: 'pre-wrap', color: '#374151', fontSize: 13, maxWidth: 240 }}>
        {estado === 'observado' ? item.observacion : ''}
      </td>
      <td style={{ minWidth: 220, position: 'relative' }}>
        {escribiendoObs ? (
          <div className="calidad-edit-panel">
            <textarea
              placeholder="Describe lo encontrado a revisar…"
              value={textoObs}
              onChange={e => setTextoObs(e.target.value)}
              disabled={guardando}
            />
            <div className="calidad-edit-panel-actions">
              <button className="calidad-btn-secondary" onClick={() => setEscribiendoObs(false)} disabled={guardando}>
                Cancelar
              </button>
              <button className="calidad-btn-primary" onClick={guardarObservacion} disabled={guardando || !textoObs.trim()}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : estado === 'pendiente' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button className="calidad-btn-editar calidad-btn-editar-verde" onClick={marcarAprobado} disabled={guardando}>
              {guardando ? 'Guardando…' : '✓ Aprobado'}
            </button>
            <button className="calidad-btn-editar" onClick={abrirObservacion} disabled={guardando}>
              ✕ Observación
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <button
                className="calidad-btn-menu"
                onClick={() => setMenuAbierto(v => !v)}
                disabled={guardando}
                title="Más opciones"
              >
                ⋮
              </button>
              {menuAbierto && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setMenuAbierto(false)} />
                  <div className="calidad-dropdown">
                    {estado === 'observado' && (
                      <button className="calidad-dropdown-item" onClick={abrirObservacion}>
                        Editar observación
                      </button>
                    )}
                    {estado === 'observado' && (
                      <button className="calidad-dropdown-item" onClick={marcarAprobado}>
                        Marcar aprobado
                      </button>
                    )}
                    <button className="calidad-dropdown-item" onClick={volverAPendiente}>
                      Volver a pendiente
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {error && <span style={{ color: '#dc2626', fontSize: 11, display: 'block', marginTop: 4 }}>{error}</span>}
      </td>
    </tr>
  );
}

function GrupoSeccion({ grupo, onActualizarProtocolo }) {
  const [colapsado, setColapsado] = useState(false);

  const stats = useMemo(() => {
    let total = 0, aprobadas = 0, observadas = 0;
    for (const partida of grupo.partidas) {
      for (const protocolo of partida.protocolos) {
        total++;
        const estado = estadoProtocolo(partida.act, protocolo).estado || 'pendiente';
        if (estado === 'aprobado') aprobadas++;
        if (estado === 'observado') observadas++;
      }
    }
    return { total, aprobadas, observadas };
  }, [grupo.partidas]);

  return (
    <div className="calidad-grupo">
      <div className="calidad-grupo-header" onClick={() => setColapsado(c => !c)}>
        <span className="calidad-grupo-nombre">{grupo.nombre}</span>
        <div className="calidad-grupo-stats">
          <span className="calidad-chip calidad-chip-total">{stats.total}</span>
          {stats.aprobadas > 0 && (
            <span className="calidad-chip calidad-chip-ok">{stats.aprobadas} aprobados</span>
          )}
          {stats.observadas > 0 && (
            <span className="calidad-chip calidad-chip-atrasada">{stats.observadas} observados</span>
          )}
          <span className="calidad-grupo-toggle">{colapsado ? '▶' : '▼'}</span>
        </div>
      </div>

      {!colapsado && (
        <div style={{ overflowX: 'auto' }}>
          <table className="calidad-tabla">
            <thead>
              <tr>
                <th>Partida</th>
                <th>Protocolo</th>
                <th>Estado</th>
                <th>Cargo(s)</th>
                <th>Observación</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {grupo.partidas.map(({ act, protocolos, cargosAsignados }) => (
                protocolos.map((protocolo, i) => (
                  <FilaRegistro
                    key={`${act._id}__${protocolo}`}
                    act={act}
                    protocolo={protocolo}
                    cargosAsignados={cargosAsignados}
                    onActualizarProtocolo={onActualizarProtocolo}
                    primeraFila={i === 0}
                    rowSpan={protocolos.length}
                  />
                ))
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function RegistrosCalidad() {
  const [datos, setDatos] = useState({ programa: null, secciones: [], actividades: [] });
  const [actividadesRegistro, setActividadesRegistro] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [filtroCargo, setFiltroCargo] = useState('todos');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [prog, config] = await Promise.all([obtenerPrograma(), obtenerConfigRegistroCalidad()]);
      setDatos(prog);
      setActividadesRegistro(config);
    } catch {
      // silencioso
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const actualizarProtocolo = useCallback(async (act, protocolo, cambios) => {
    const actual = act.registroCalidadProtocolos || [];
    const idx = actual.findIndex(p => p.protocolo === protocolo);
    const nuevoArreglo = idx >= 0
      ? actual.map((p, i) => i === idx ? { ...p, ...cambios } : p)
      : [...actual, { protocolo, estado: 'pendiente', observacion: '', ...cambios }];

    const actualizada = await actualizarAvanceCalidad(datos.programa._id, act._id, {
      registroCalidadProtocolos: nuevoArreglo
    });
    setDatos(prev => ({
      ...prev,
      actividades: prev.actividades.map(a => a._id === act._id ? { ...a, ...actualizada } : a)
    }));
  }, [datos.programa]);

  const mapaConfigPorWbs = useMemo(() => {
    const m = {};
    for (const item of actividadesRegistro) {
      m[item.wbsCode] = { protocolos: item.protocolos || [], cargosAsignados: item.cargosAsignados || [] };
    }
    return m;
  }, [actividadesRegistro]);

  const grupos = useMemo(() => {
    const mapaSeccion = {};
    for (const s of datos.secciones) {
      if (s.wbsCode) mapaSeccion[s.wbsCode] = s;
    }
    const mapaAct = {};
    for (const act of datos.actividades) {
      if (act.wbsCode) mapaAct[act.wbsCode] = act.nombre;
    }

    const getRuta = (wbsCode) => {
      const partes = wbsCode.split('.');
      const ruta = [];
      for (let i = 1; i < partes.length; i++) {
        const code = partes.slice(0, i).join('.');
        const nombre = i === 1
          ? mapaSeccion[code]?.nombre
          : mapaAct[code];
        if (nombre) ruta.push(nombre);
      }
      return ruta;
    };

    const mapa = {};
    for (const act of datos.actividades) {
      const config = act.wbsCode ? mapaConfigPorWbs[act.wbsCode] : undefined;
      if (!config || config.protocolos.length === 0) continue;
      const nivel1 = act.wbsCode.split('.')[0];
      const actConRuta = { ...act, _rutaGrupos: getRuta(act.wbsCode).slice(-3) };

      const protocolosFiltrados = config.protocolos.filter(protocolo => {
        const estado = estadoProtocolo(actConRuta, protocolo).estado || 'pendiente';
        if (filtroCargo === 'supervisor' && estado !== 'observado') return false;
        if (filtroCargo !== 'todos' && filtroCargo !== 'supervisor' && !config.cargosAsignados.includes(filtroCargo)) return false;
        return true;
      });
      if (protocolosFiltrados.length === 0) continue;

      if (!mapa[nivel1]) {
        const sec = mapaSeccion[nivel1];
        mapa[nivel1] = { nombre: sec?.nombre || nivel1, orden: sec?.orden ?? 9999, partidas: [] };
      }
      mapa[nivel1].partidas.push({ act: actConRuta, protocolos: protocolosFiltrados, cargosAsignados: config.cargosAsignados });
    }
    return Object.values(mapa).sort((a, b) => a.orden - b.orden);
  }, [datos.actividades, datos.secciones, mapaConfigPorWbs, filtroCargo]);

  if (cargando) {
    return <div className="calidad-loading">Cargando registros…</div>;
  }

  if (!datos.programa) {
    return (
      <div className="calidad-container">
        <div className="calidad-empty">
          <div className="calidad-empty-icon">📋</div>
          <h3>No hay programa de obra importado</h3>
          <p>Importa un programa en el módulo Programa de Obra para usar esta vista.</p>
        </div>
      </div>
    );
  }

  if (actividadesRegistro.length === 0) {
    return (
      <div className="calidad-container">
        {mostrarConfig && (
          <ConfiguracionRegistros
            actividades={datos.actividades}
            secciones={datos.secciones}
            actividadesActuales={actividadesRegistro}
            onGuardado={(nuevas) => { setActividadesRegistro(nuevas); setMostrarConfig(false); }}
            onCerrar={() => setMostrarConfig(false)}
          />
        )}
        <div className="calidad-empty">
          <div className="calidad-empty-icon">🗂️</div>
          <h3>Sin partidas configuradas</h3>
          <p>Selecciona qué partidas del programa vas a controlar en Registros y define sus protocolos.</p>
          <button className="calidad-btn-primary" onClick={() => setMostrarConfig(true)}>
            Seleccionar partidas de Registros
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="calidad-container">
      {mostrarConfig && (
        <ConfiguracionRegistros
          actividades={datos.actividades}
          secciones={datos.secciones}
          actividadesActuales={actividadesRegistro}
          onGuardado={(nuevas) => { setActividadesRegistro(nuevas); setMostrarConfig(false); }}
          onCerrar={() => setMostrarConfig(false)}
        />
      )}

      <div className="calidad-header">
        <h2>Registros</h2>
        <div className="calidad-header-actions">
          <button className="calidad-btn-secondary" onClick={() => setMostrarConfig(true)}>
            ⚙ Configurar partidas
          </button>
        </div>
      </div>

      <div className="calidad-filtros">
        <button
          className={`calidad-filtro-chip ${filtroCargo === 'todos' ? 'activo' : ''}`}
          onClick={() => setFiltroCargo('todos')}
        >
          Todos
        </button>
        {CARGOS_CONTROL.map(cargo => (
          <button
            key={cargo}
            className={`calidad-filtro-chip ${filtroCargo === cargo ? 'activo' : ''}`}
            onClick={() => setFiltroCargo(cargo)}
          >
            {cargo}
          </button>
        ))}
        <button
          className={`calidad-filtro-chip ${filtroCargo === 'supervisor' ? 'activo' : ''}`}
          onClick={() => setFiltroCargo('supervisor')}
        >
          Supervisor (observados)
        </button>
      </div>

      {grupos.length === 0 ? (
        <div className="calidad-empty">
          <div className="calidad-empty-icon">🔍</div>
          <h3>Sin partidas en este filtro</h3>
          <p>Prueba con otro cargo o vuelve a "Todos".</p>
        </div>
      ) : (
        grupos.map(grupo => (
          <GrupoSeccion key={grupo.nombre} grupo={grupo} onActualizarProtocolo={actualizarProtocolo} />
        ))
      )}
    </div>
  );
}
